// The boilerplate every chart config composable repeats, in one place.
//
// A config composable's job is SQL (rule 2: domain SQL lives per-app). Everything
// AROUND the SQL — reading a filter context that may be a ref or a plain object,
// running a query, registering/removing this chart's filter, and the drill scope
// that keeps a terminal status click inside the level the user navigated into —
// is identical in all ten of them. That part lives here; **no SQL string ever
// does**.
//
// ```js
// const { run, applyFilter, drillScope, reset } =
//   makeChartConfig({ chartName, filterContext, conn });
// ```
//
// A config with extra closure state wraps `reset`:
//
// ```js
// reset: async () => { selectedType.value = null; await reset(); }
// ```

import { unref } from 'vue';
import { queryRows } from '@rdrive/data-duckdb';
import { executeAndProcessQuery, makeDrillScope } from './stackedBarChartData.js';

/**
 * @param {object} options
 * @param {string} options.chartName unique per chart INSTANCE — it keys both the filter
 *   registry and a DuckDB temp view (`filter_view_<chartName>`)
 * @param {object|import('vue').Ref} options.filterContext the filter-context duck type,
 *   as a ref/computed (store-backed pages) or a plain object
 * @param {object} options.conn the DuckDB connection
 * @returns {{
 *   ctx: () => object,
 *   run: (sql: string) => Promise<object[]>,
 *   rows: (sql: string) => Promise<object[]>,
 *   applyFilter: (sql: string, label?: string) => Promise<void>,
 *   removeFilter: () => Promise<void>,
 *   drillScope: ReturnType<typeof makeDrillScope>,
 *   reset: () => Promise<void>
 * }}
 */
export function makeChartConfig({ chartName, filterContext, conn }) {
  // Read the context LIVE on every call — a page may swap the instance
  const ctx = () => unref(filterContext);
  const drillScope = makeDrillScope();
  const removeFilter = () => ctx().removeFilter(chartName);

  return {
    ctx,
    // → the stacked bar chart's nested row shape
    run: (sql) => executeAndProcessQuery(conn, sql),
    // → plain rows (the donut, a time series)
    rows: (sql) => queryRows(conn, sql),
    // `label` (opt-in, AM_ABSORPTION_PLAN C3): the filter's chip text — the filter context's
    // optional last argument (@rdrive/data-filters, `getActiveFilters()`). Passed ONLY when
    // defined, so a call without one is the two-argument call it always was.
    applyFilter: (sql, label) =>
      label === undefined
        ? ctx().updateFilterWithQuery(chartName, sql)
        : ctx().updateFilterWithQuery(chartName, sql, label),
    removeFilter,
    drillScope,
    // The default `contextUpdaters.reset`; wrap it when the config holds state
    reset: async () => {
      drillScope.reset();
      await removeFilter();
    },
  };
}
